// Import Dependencies
import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import includes from 'lodash/includes';

import { gtm, createSignupProgressEvent, clickCtaEvent, upgradeModalEvent } from '~/common/utils/tracking.js';
import { premiumAreaCodes } from '~/common/utils/premiumAreaCodes.js';
import { PATH_PLANS, PATH_TRIAL_NUMBERS, PATH_LOCAL_RECOMMEND, PATH_LOCAL_SEARCH } from '~/common/utils/consts.js';
import starIcon from '~/common/images/img-icon-star.svg';
// Import Local Styles
import styles from './upgrade-modal.css';
import Button from '../CommonButton';

@withRouter
@inject('lockNumberStore', 'modalStore', 'trialSignupStore', 'offerSuiteStore', 'lockNumberStore')
@observer
export default class UpgradeModal extends Component {
    componentDidMount() {
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });

        // track signup progress event: user has picked a premium number or starting trial flow
        gtm(createSignupProgressEvent('trial-intent', 'premium-overlay'));
        // track Upgrade Modal rendered
        gtm(upgradeModalEvent());
    }

    pickDifferentPlan() {
        const { modalStore, trialSignupStore, history, lockNumberStore } = this.props;

        modalStore.setModalOptions({ isModalOpen: false });

        trialSignupStore.setIsTrialFlow(false);
        lockNumberStore.lockNumberToSession();

        const ctaText = trialSignupStore.isTrialFlow ?
            'Upgrade'
            :
            'Select a paid plan';

        gtm(clickCtaEvent(ctaText, PATH_PLANS, 'upgrade modal'));

        history.push(PATH_PLANS);
    }

    pickDifferentNumber() {
        const { lockNumberStore, modalStore, offerSuiteStore, trialSignupStore, history } = this.props;

        // wipe whatever number the user has selected entirely
        if (lockNumberStore.numberIsSelected) {
            lockNumberStore.flushSelectedNumber();
            lockNumberStore.setNumberIsLocked(false);
            lockNumberStore.setNumberIsLockedUI(false);
        }

        const ctaText = trialSignupStore.isTrialFlow ?
            'Upgrade'
            :
            'Select a paid plan';

        // user is in a premium area code - send them straight to Local Search;
        if (includes(history.location.pathname, PATH_LOCAL_RECOMMEND)) {
            gtm(clickCtaEvent(ctaText, PATH_LOCAL_SEARCH, 'upgrade modal'));
            history.push(PATH_LOCAL_SEARCH);
        } else if (offerSuiteStore.selectedOfferId) {
            // User bailed from Premium flow, direct them into Trial flow
            offerSuiteStore.setSelectedOfferSet(null);
            trialSignupStore.setIsTrialFlow(true);
            gtm(clickCtaEvent(ctaText, PATH_TRIAL_NUMBERS, 'upgrade modal'));
            history.push(PATH_TRIAL_NUMBERS);
        }
        // User just tried to pick trial with a premium number, send them to numbers step
        if (!trialSignupStore.isTrialFlow) {
            trialSignupStore.setIsTrialFlow(true);
            gtm(clickCtaEvent(ctaText, PATH_TRIAL_NUMBERS, 'upgrade modal'));
            history.push(PATH_TRIAL_NUMBERS);
        }

        modalStore.setModalOptions({ isModalOpen: false });
    }

    renderIcon() {
        return (
            <img src={starIcon}></img>
        );
    }

    renderHeader() {
        const { trialSignupStore } = this.props;

        const elementText = trialSignupStore.isTrialFlow ?
            // Trial: Selected, Premium because: premium prefix number selected
            'Upgrade to get this area code.'
            :
            // Trial: Not selected yet, Premium because: premium prefix number locked
            'Upgrade to get this number.';

        return <h2 className={styles.header}>{elementText}</h2>;
    }

    renderSummary() {
        const { lockNumberStore, trialSignupStore } = this.props;

        const elementText = trialSignupStore.isTrialFlow ?
            lockNumberStore.selectedNumberType === 'Local' ?
                // Trial: Selected, Premium because: premium prefix number selected, Number type: Local
                `Area code ${lockNumberStore.selectedNumberPrefix} is a premium* area code, and is only available in the paid version of Grasshopper.`
                :
                // Trial: Selected, Premium because: premium prefix number selected, Number type: Toll Free
                '800 numbers are only available in the paid version of Grasshopper.'
            :
            // Trial: Not selected yet, Premium because: premium prefix number locked, Number type: Toll Free or Local
            `The number you selected, ${lockNumberStore.selectedNumberUI}, uses a premium* area code, and is only available in the paid version of Grasshopper.`;

        return <p className={styles.text}>{elementText}</p>;
    }

    renderButton() {
        const { trialSignupStore } = this.props;

        const elementText = trialSignupStore.isTrialFlow ?
            'Upgrade'
            :
            'Select a paid plan';

        return (
            <Button
                id={`upgrade-modal__${trialSignupStore.isTrialFlow ? 'upgrade-to-paid' : 'select-plan'}`}
                modifier={styles.button}
                onClick={ev => this.pickDifferentPlan(ev)}
            >{elementText}</Button>
        );
    }

    renderLink() {
        const { trialSignupStore } = this.props;

        const elementText = trialSignupStore.isTrialFlow ?
            // User is already in Trial flow
            'Pick a different number.'
            :
            // User already locked a number before pressing Trial button
            'I want a trial, I\'ll pick a different number';

        return (
            <Button
                id={`upgrade-modal__${trialSignupStore.isTrialFlow ? 'upgrade-number' : 'select-trial'}`}
                modifier={styles.link}
                onClick={ev => this.pickDifferentNumber(ev)}
                tertiary
            >{elementText}</Button>
        );
    }

    renderDetail() {
        const { lockNumberStore, trialSignupStore } = this.props;

        if (trialSignupStore.isTrialFlow && lockNumberStore.selectedNumberType !== 'Local') return null;

        const { localUS, localCanada, tollFree } = premiumAreaCodes;

        return trialSignupStore.isTrialFlow ?
            <div
                className={`${styles.text} ${styles['text--small']}`}
            >
                <p>*Premium area codes include:</p>
                <p><span className={styles.highlight}>US Local</span>: {localUS.join(', ')}. <span className={styles.highlight}>Canada</span>: {localCanada.join(', ')}.</p>
            </div>
            :
            <div
                className={`${styles.text} ${styles['text--small']} ${styles['text--no-bottom']}`}
            >
                <p>*Premium area codes include:</p>
                <p><span className={styles.highlight}>US Local</span>: {localUS.join(', ')}.</p>
                <p><span className={styles.highlight}>Toll free</span>: {tollFree.join(', ')}. <span className={styles.highlight}>Canada</span>: {localCanada.join(', ')}.</p>
            </div>;
    }

    render() {
        return (
            <div className={styles.wrapper}>
                {this.renderIcon()}
                <div className={styles.content}>
                    {this.renderHeader()}
                    {this.renderSummary()}
                    {this.renderButton()}
                    {this.renderLink()}
                    {this.renderDetail()}
                </div>
            </div>
        );
    }
}
